
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('🔐 [AuthContext] Initializing auth listener');
    
    let debounceTimer: NodeJS.Timeout | null = null;
    
    // Helper function to update last seen timestamp (runs asynchronously, non-blocking)
    const updateLastSeen = async (userId: string) => {
      try {
        console.log('🔐 [AuthContext] Updating last seen for user:', userId);
        const { error } = await supabase
          .from('profiles')
          .update({ last_seen_at: new Date().toISOString() })
          .eq('user_id', userId);
        
        if (error) {
          console.error('🔐 [AuthContext] Error updating last seen:', error);
          // Fail silently - this is non-critical
        }
      } catch (e) {
        console.error('🔐 [AuthContext] Exception updating last seen:', e);
      }
    };

    // Helper function to check if user profile is archived (runs asynchronously)
    const checkAndSignOutIfArchived = async (userId: string) => {
      try {
        console.log('🔐 [AuthContext] Checking archived status for user:', userId);
        const { data, error } = await supabase
          .from('profiles')
          .select('archived')
          .eq('user_id', userId)
          .maybeSingle();
        
        if (error) {
          console.error('🔐 [AuthContext] Error checking archived status:', error);
          return; // Fail open - don't block if check fails
        }
        
        // Check if profile doesn't exist (permanently deleted user)
        if (data === null) {
          console.log('🚫 [AuthContext] Profile not found (account removed), signing out');
          toast.error("Your account has been removed. Please contact support if you believe this is an error.");
          await supabase.auth.signOut({ scope: 'local' });
          setSession(null);
          setUser(null);
          return;
        }
        
        if (data?.archived === true) {
          console.log('🚫 [AuthContext] User is archived, signing out');
          await supabase.auth.signOut({ scope: 'local' });
          setSession(null);
          setUser(null);
        }
      } catch (e) {
        console.error('🔐 [AuthContext] Exception checking archived status:', e);
      }
    };

    // Set up auth state listener - NOT async to prevent deadlock
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('🔐 [AuthContext] Auth state changed:', event, session?.user?.email);
        
        // Log token refresh events explicitly
        if (event === 'TOKEN_REFRESHED') {
          console.log('✅ [AuthContext] Token refreshed successfully');
        }
        
        if (event === 'SIGNED_OUT' || !session) {
          console.log('⚠️ [AuthContext] Session expired or signed out');
          
          // Clear any pending debounce
          if (debounceTimer) {
            clearTimeout(debounceTimer);
            debounceTimer = null;
          }
          
          // Force clear any lingering localStorage data
          try {
            localStorage.removeItem('sb-ndqudoynysmynztrepwj-auth-token');
            console.log('🧹 [AuthContext] Cleared localStorage auth token');
          } catch (e) {
            console.error('❌ [AuthContext] Failed to clear localStorage:', e);
          }
          
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }
        
        // Clear any pending updates
        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }
        
        // Debounce rapid auth state changes (especially during initialization)
        // Session is set IMMEDIATELY for all users - no blocking
        debounceTimer = setTimeout(() => {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
          
          // AFTER session is set, check archived status asynchronously using setTimeout(0)
          // This prevents Supabase deadlock by deferring the DB call
          if ((event === 'INITIAL_SESSION' || event === 'SIGNED_IN') && session?.user?.id) {
            setTimeout(() => {
              checkAndSignOutIfArchived(session.user.id);
              updateLastSeen(session.user.id);
              // Claim a pending invitation server-side (any sign-in method). Safe to repeat.
              const pendingToken =
                sessionStorage.getItem("pending_invite_token") ||
                (session.user.user_metadata as any)?.invitation_token;
              if (pendingToken) {
                (supabase.rpc as any)("accept_user_invitation", { _token: pendingToken })
                  .then(({ error }: any) => {
                    if (error) console.warn("[Invite] accept_user_invitation:", error.message);
                    sessionStorage.removeItem("pending_invite_token");
                  });
              }
            }, 0);
          }
        }, 50);
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('🔐 [AuthContext] Initial session check:', session?.user?.email);
      
      // Set session immediately - no blocking
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Check archived status asynchronously AFTER session is set
      if (session?.user?.id) {
        setTimeout(() => {
          checkAndSignOutIfArchived(session.user.id);
          updateLastSeen(session.user.id);
        }, 0);
      }
    });

    return () => {
      console.log('🔐 [AuthContext] Cleaning up auth listener');
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    console.log('🔐 [AuthContext] Signing out user');
    setLoading(true);
    
    try {
      // Use scope: 'local' to only clear local session
      // This doesn't require server validation and always succeeds
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      
      if (error) {
        console.error('❌ [AuthContext] Logout error (will clear local state anyway):', error);
      }
      
      // Always clear state, even if logout "failed"
      setSession(null);
      setUser(null);
      
      console.log('✅ [AuthContext] Local session cleared successfully');
    } catch (error) {
      console.error('❌ [AuthContext] Unexpected logout error:', error);
      // Still clear state on any error
      setSession(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    session,
    loading,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
